/**
 * ComplianceBoard — Kanban view for compliance items (CompliancePage board tab).
 * Columns = the compliance item status enum; cards link to item detail.
 * Native HTML5 drag-and-drop between columns → PATCH status via the existing
 * endpoint (server enforces compliance.edit + Completed lock). Completed column
 * respects the hide-completed default: collapsed placeholder until expanded.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { ChevronDown, ChevronRight, Paperclip } from 'lucide-react';
import {
  itemStatusSchema, ITEM_STATUS_LABELS, DATA_STATUS_LABELS,
  documentRenewalDays, documentRenewalLabel, formatDocumentExpiry,
  type ComplianceItemOutput, type ItemStatus, type DataStatus,
} from '@1financial/shared';
import { SkeletonBlock } from './Skeleton.js';
import { EmptyState } from './EmptyState.js';
import { ClipboardCheck } from 'lucide-react';
import { fmtDate } from '../lib/date.js';

const STATUS_PILL: Record<string, string> = {
  Pending: 'pill-neutral',
  InProcess: 'pill-info',
  ReadyForReview: 'pill-warn',
  Reviewed: 'pill-indigo',
  SentBack: 'pill-danger',
  SentToClient: 'pill-teal',
  Completed: 'pill-success',
};

export interface BoardClientInfo {
  name: string;
  clientCode: string;
}

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function deadlineClass(deadline: string | null): string {
  if (!deadline) return 'text-muted';
  const d = new Date(deadline);
  const today = startOfToday();
  if (d < today) return 'text-rose-400 font-medium';
  if (d.getTime() - today.getTime() <= 3 * 86400e3) return 'text-amber-300';
  return 'text-muted';
}

function deadlineLabel(deadline: string | null): string {
  if (!deadline) return 'No deadline';
  return fmtDate(deadline);
}

function BoardCard({
  item,
  client,
  assigneeNames,
  canDrag,
  dragging,
  onDragStart,
  onDragEnd,
  onMove,
}: {
  item: ComplianceItemOutput;
  client: BoardClientInfo | undefined;
  assigneeNames: string[];
  canDrag: boolean;
  dragging: boolean;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  /** Touch fallback: HTML5 drag-and-drop never fires on touchscreens, so coarse pointers get an explicit status picker instead. */
  onMove: (status: string) => void;
}) {
  return (
    <div
      draggable={canDrag}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', item.id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(item.id);
      }}
      onDragEnd={onDragEnd}
      className={clsx(
        'bg-bg-card border border-border rounded-lg p-3 space-y-1.5 transition-opacity',
        canDrag && 'cursor-grab active:cursor-grabbing hover:border-accent/50',
        dragging && 'opacity-40',
      )}
    >
      <Link to={`/compliance/${item.id}`} className="block text-sm text-white hover:text-accent font-medium leading-snug">
        {item.serviceType}
      </Link>
      <div className="text-xs text-muted truncate">
        {item.client?.name ?? client?.name ?? '—'}
        {item.jurisdiction ? ` · ${item.jurisdiction}` : ''}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        {item.documentRenewal && item.documentRenewalExpiryDate && item.status !== 'Completed' ? (
          <span className={documentRenewalDays(item.documentRenewalExpiryDate) < 0 ? 'text-rose-400 font-medium' : 'text-amber-300'}>
            {formatDocumentExpiry(item.documentRenewalExpiryDate)}
            <span className="block">{documentRenewalLabel(item.documentRenewalExpiryDate)}</span>
          </span>
        ) : (
          <span className={item.status === 'Completed' ? 'text-muted' : deadlineClass(item.dueDate)}>{deadlineLabel(item.dueDate)}</span>
        )}
        {item.dataStatus && (
          <span className="text-muted">{DATA_STATUS_LABELS[item.dataStatus as DataStatus] ?? item.dataStatus}</span>
        )}
        {item.documentRenewal && (
          <span
            title="Document renewal — auto-created from an expiring document; confirming the renewed copy closes it"
            className="inline-flex items-center text-[10px] font-semibold border pill-neutral rounded px-1 py-0.5"
          >
            R
          </span>
        )}
        {item.evidenceCount > 0 && (
          <span
            title={`${item.evidenceCount} evidence file${item.evidenceCount === 1 ? '' : 's'} filed`}
            className="inline-flex items-center gap-0.5 text-muted"
          >
            <Paperclip size={11} />{item.evidenceCount}
          </span>
        )}
      </div>
      {assigneeNames.length > 0 && (
        <div className="text-xs text-muted truncate">{assigneeNames.join(' · ')}</div>
      )}
      {canDrag && (
        <select
          value={item.status}
          onChange={(e) => { if (e.target.value !== item.status) onMove(e.target.value); }}
          aria-label="Move card to another column"
          className="hidden [@media(pointer:coarse)]:block w-full bg-bg-base border border-border rounded-md px-1.5 py-1.5 text-xs text-white"
        >
          {itemStatusSchema.options.map((s) => (
            <option key={s} value={s}>{ITEM_STATUS_LABELS[s as ItemStatus] ?? s}</option>
          ))}
        </select>
      )}
    </div>
  );
}

export function ComplianceBoard({
  items,
  isLoading,
  error,
  clientMap,
  userName,
  canEdit,
  canEditCompleted,
  onChangeStatus,
  pendingIds,
  completedItems,
  completedLoading,
  showCompleted,
  onToggleCompleted,
}: {
  items: ComplianceItemOutput[] | undefined;
  isLoading: boolean;
  error: Error | null;
  clientMap: Map<string, BoardClientInfo>;
  userName: (id: string | null) => string | null;
  canEdit: boolean;
  canEditCompleted: boolean;
  onChangeStatus: (id: string, status: string) => void;
  pendingIds: Set<string>;
  completedItems: ComplianceItemOutput[] | undefined;
  completedLoading: boolean;
  showCompleted: boolean;
  onToggleCompleted: () => void;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  if (isLoading) return <SkeletonBlock rows={4} />;
  if (error) return <div className="bg-bg-panel border border-border rounded-xl p-8 text-center text-rose-400 text-sm">{error.message}</div>;
  const all = items ?? [];
  if (all.length === 0 && !showCompleted) {
    return (
      <div className="bg-bg-panel border border-border rounded-xl">
        <EmptyState icon={ClipboardCheck} title="No compliance items match these filters." />
      </div>
    );
  }

  const visibleStatuses = itemStatusSchema.options.filter((s) => s !== 'Completed');
  const draggingItem = dragId ? all.find((i) => i.id === dragId) ?? completedItems?.find((i) => i.id === dragId) : null;

  function columnCards(status: string) {
    const source = status === 'Completed' ? (completedItems ?? []) : all;
    return source.filter((i) => i.status === status);
  }

  function renderColumn(status: string, collapsed?: boolean) {
    const cards = columnCards(status);
    const pill = STATUS_PILL[status] ?? 'pill-neutral';
    return (
      <div
        key={status}
        onDragOver={(e) => {
          if (!dragId || !draggingItem || draggingItem.status === status) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setDropTarget(status);
        }}
        onDragLeave={() => setDropTarget((t) => (t === status ? null : t))}
        onDrop={(e) => {
          e.preventDefault();
          setDropTarget(null);
          const id = e.dataTransfer.getData('text/plain') || dragId;
          if (id && draggingItem && draggingItem.status !== status) onChangeStatus(id, status);
          setDragId(null);
        }}
        className={clsx(
          'flex-shrink-0 w-72 rounded-xl border bg-bg-panel transition-colors',
          dropTarget === status ? 'border-accent' : 'border-border',
        )}
      >
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium ${pill}`}>
            {ITEM_STATUS_LABELS[status as ItemStatus] ?? status}
          </span>
          <span className="text-xs text-muted">{collapsed ? (completedLoading ? '…' : cards.length) : cards.length}</span>
        </div>
        <div className="p-2 space-y-2 min-h-[4rem] max-h-[65vh] overflow-y-auto">
          {cards.map((item) => {
            const locked = item.status === 'Completed' && !canEditCompleted;
            const assignees = [userName(item.assignee1Id), userName(item.assignee2Id)].filter((n): n is string => n !== null);
            return (
              <BoardCard
                key={item.id}
                item={item}
                client={clientMap.get(item.clientId)}
                assigneeNames={assignees}
                canDrag={canEdit && !locked && !pendingIds.has(item.id)}
                dragging={dragId === item.id}
                onDragStart={setDragId}
                onDragEnd={() => { setDragId(null); setDropTarget(null); }}
                onMove={(status) => onChangeStatus(item.id, status)}
              />
            );
          })}
          {cards.length === 0 && <p className="text-xs text-muted italic text-center py-3">No items</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto pb-2">
      <div className="flex gap-3 min-w-max">
        {visibleStatuses.map((s) => renderColumn(s))}
        {/* Completed respects the hide-completed default: collapsed until expanded. */}
        <div className="flex-shrink-0 w-72 rounded-xl border border-border bg-bg-panel">
          <button
            onClick={onToggleCompleted}
            className="w-full flex items-center justify-between px-3 py-2.5 text-left"
          >
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-xs font-medium pill-success">
              {showCompleted ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              Completed
            </span>
            <span className="text-xs text-muted">{showCompleted ? 'hide' : 'show'}</span>
          </button>
          {showCompleted && (
            <div className="border-t border-border">
              {completedLoading ? (
                <div className="p-3"><SkeletonBlock rows={2} /></div>
              ) : (
                <div className="p-2 space-y-2 max-h-[65vh] overflow-y-auto">
                  {(completedItems ?? []).map((item) => {
                    const locked = !canEditCompleted;
                    const assignees = [userName(item.assignee1Id), userName(item.assignee2Id)].filter((n): n is string => n !== null);
                    return (
                      <BoardCard
                        key={item.id}
                        item={item}
                        client={clientMap.get(item.clientId)}
                        assigneeNames={assignees}
                        canDrag={canEdit && !locked && !pendingIds.has(item.id)}
                        dragging={dragId === item.id}
                        onDragStart={setDragId}
                        onDragEnd={() => { setDragId(null); setDropTarget(null); }}
                        onMove={(status) => onChangeStatus(item.id, status)}
                      />
                    );
                  })}
                  {(completedItems ?? []).length === 0 && <p className="text-xs text-muted italic text-center py-3">No completed items</p>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
